import type { ProductCategory } from '../../../types/inventory';
import { resolveProductPackagingFields } from '../../../utils/productPackaging';
import { DEFAULT_BUSINESS_CONFIGURATION } from '../../../types/business';

const DEFAULT_IMPORT_MIN_STOCK =
  DEFAULT_BUSINESS_CONFIGURATION.inventory.lowStockThreshold;

/** En-têtes du fichier modèle / export (ordre fixe). */
export const PRODUCT_IMPORT_EXPORT_HEADERS = [
  'Nom',
  'Catégorie (nom)',
  'Code-barres',
  'Prix vente',
  'Prix achat',
  'Seuil alerte stock',
  'Stock',
  'Marque',
  'Description',
  'Conditionnement (valeur)',
  'Conditionnement (unité)',
] as const;

export type ProductImportDraftRow = {
  rowIndex: number;
  /** Texte lu dans le fichier (informationnel). */
  importCategoryName: string;
  /** Sélection utilisateur ou match auto ; obligatoire pour valider la ligne. */
  selectedCategoryId?: string;
  /** Produit de la boutique source à utiliser pour compléter prix / marque / description (import multi-boutiques). */
  selectedSourceProductId?: string;
  /** L'utilisateur a explicitement supprimé le produit de référence : ne pas ré-auto-sélectionner. */
  sourceMatchDisabled?: boolean;
  name: string;
  barcode: string;
  sellingPrice?: number;
  costPrice?: number;
  minStockAlert: number;
  currentStock: number;
  brand: string;
  description: string;
  packagingValue?: number;
  packagingUnit?: string;
};

function norm(s: string): string {
  return s.trim().toLowerCase();
}

/** Trouve l'id de catégorie si le nom correspond exactement (trim, insensible à la casse / accents). */
export function matchCategoryIdByName(
  categories: ProductCategory[],
  categoryNameFromFile: string
): string | undefined {
  const t = norm(categoryNameFromFile);
  if (!t) return undefined;
  const found = categories.find((c) => norm(c.name) === t);
  return found?.id;
}

function parseOptionalNumber(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.').trim());
  if (!Number.isFinite(n)) return undefined;
  return n;
}

function parseRequiredInt(raw: unknown, fallback: number): number {
  const n = parseOptionalNumber(raw);
  if (n === undefined) return fallback;
  return Math.round(n);
}

/** Clés des champs import produits (correspondance colonnes). */
export type ProductImportFieldKey =
  | 'name'
  | 'category'
  | 'barcode'
  | 'sellingPrice'
  | 'costPrice'
  | 'minStockAlert'
  | 'currentStock'
  | 'brand'
  | 'description'
  | 'packagingValue'
  | 'packagingUnit';

export const PRODUCT_IMPORT_FIELD_LABELS: Record<ProductImportFieldKey, string> = {
  name: 'Nom du produit',
  category: 'Catégorie (nom)',
  barcode: 'Code-barres',
  sellingPrice: 'Prix de vente',
  costPrice: "Prix d'achat",
  minStockAlert: 'Seuil alerte stock',
  currentStock: 'Stock',
  brand: 'Marque',
  description: 'Description',
  packagingValue: 'Conditionnement (valeur)',
  packagingUnit: 'Conditionnement (unité)',
};

export const PRODUCT_IMPORT_REQUIRED_FIELDS: ProductImportFieldKey[] = ['name', 'category'];

export const PRODUCT_IMPORT_FIELD_KEYS: ProductImportFieldKey[] = [
  'name',
  'category',
  'barcode',
  'sellingPrice',
  'costPrice',
  'minStockAlert',
  'currentStock',
  'brand',
  'description',
  'packagingValue',
  'packagingUnit',
];

/** Deux champs mappés sur la même colonne Excel. */
export function validateColumnMappingDuplicates(mapping: ProductImportColumnMapping): string | null {
  const byCol = new Map<number, ProductImportFieldKey[]>();
  (Object.entries(mapping) as [ProductImportFieldKey, number | undefined][]).forEach(([key, col]) => {
    if (col === undefined) return;
    if (!byCol.has(col)) byCol.set(col, []);
    byCol.get(col)!.push(key);
  });
  for (const [col, keys] of Array.from(byCol.entries())) {
    if (keys.length > 1) {
      const labels = keys.map((k: ProductImportFieldKey) => PRODUCT_IMPORT_FIELD_LABELS[k]).join(', ');
      return `La colonne ${columnIndexToExcelLetters(col)} est utilisée plusieurs fois : ${labels}.`;
    }
  }
  return null;
}

/** Correspondance colonne Excel (index 0-based) → champ. Champs requis : name, category. */
export type ProductImportColumnMapping = {
  name: number;
  category: number;
  barcode?: number;
  sellingPrice?: number;
  costPrice?: number;
  minStockAlert?: number;
  currentStock?: number;
  brand?: number;
  description?: number;
  packagingValue?: number;
  packagingUnit?: number;
};

/** Mappe une cellule d'en-tête vers un champ connu. */
export function mapHeaderToField(headerCell: unknown): ProductImportFieldKey | null {
  const h = String(headerCell ?? '').trim();
  const n = norm(h);
  if (!n) return null;
  if (n === 'nom' || n === 'name' || n === 'produit') return 'name';
  if (
    n.includes('categorie') ||
    n.includes('category') ||
    n === 'cat' ||
    h.toLowerCase().includes('catégorie')
  ) {
    return 'category';
  }
  if (n.includes('code') && (n.includes('bar') || n.includes('ean'))) return 'barcode';
  if (n.includes('barcode') || n === 'ean') return 'barcode';
  if (n.includes('prix') && (n.includes('vente') || n.includes('sell'))) return 'sellingPrice';
  if (n.includes('prix') && (n.includes('achat') || n.includes('cost') || n.includes('cout'))) {
    return 'costPrice';
  }
  if (n.includes('seuil') || n.includes('alert') || n.includes('min stock')) return 'minStockAlert';
  if (n === 'stock' || n === 'quantite' || n === 'quantité' || n.includes('qty')) return 'currentStock';
  if (n.includes('marque') || n === 'brand') return 'brand';
  if (n.includes('description')) return 'description';
  if (n.includes('conditionnement') && n.includes('valeur')) return 'packagingValue';
  if (n.includes('conditionnement') && n.includes('unit')) return 'packagingUnit';
  if (n === 'unite' || n === 'unité') return 'packagingUnit';
  return null;
}

/** Index Excel → lettre de colonne (A, B, …, Z, AA, …). */
export function columnIndexToExcelLetters(zeroBasedIndex: number): string {
  let dividend = zeroBasedIndex + 1;
  let columnName = '';
  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }
  return columnName || 'A';
}

/** Repère la ligne qui ressemble le plus à des en-têtes (score = cellules reconnues). */
export function guessHeaderRowIndex(rows: unknown[][]): number {
  if (!rows.length) return 0;
  let best = 0;
  let bestScore = -1;
  for (let r = 0; r < Math.min(rows.length, 25); r++) {
    const line = rows[r] || [];
    let score = 0;
    for (let c = 0; c < line.length; c++) {
      if (mapHeaderToField(line[c])) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = r;
    }
  }
  return best;
}

/** Première colonne reconnue par champ (pour suggestion automatique). */
export function suggestMappingFromHeaderRow(headerRow: unknown[]): Partial<Record<ProductImportFieldKey, number>> {
  const m: Partial<Record<ProductImportFieldKey, number>> = {};
  const line = headerRow || [];
  for (let c = 0; c < line.length; c++) {
    const f = mapHeaderToField(line[c]);
    if (f && m[f] === undefined) {
      m[f] = c;
    }
  }
  return m;
}

function cellAt(line: unknown[], col: number | undefined): unknown {
  if (col === undefined || col < 0) return undefined;
  return line[col];
}

export type ProductImportParseOptions = {
  /** Si la colonne seuil est absente ou vide : valeur utilisée (souvent `business.configuration.inventory.lowStockThreshold`). */
  defaultMinStockAlert?: number;
};

/**
 * Parse les lignes données avec une correspondance colonnes explicite.
 */
export function parseProductImportMatrixWithMapping(
  rows: unknown[][],
  headerRowIndex: number,
  mapping: ProductImportColumnMapping,
  options?: ProductImportParseOptions
): ProductImportDraftRow[] {
  const minStockFallback = options?.defaultMinStockAlert ?? DEFAULT_IMPORT_MIN_STOCK;
  if (!rows.length) return [];
  const dataRows = rows.slice(headerRowIndex + 1);
  const drafts: ProductImportDraftRow[] = [];
  let rowIndex = headerRowIndex + 2;

  for (const line of dataRows) {
    const cells: Record<ProductImportFieldKey, unknown> = {
      name: cellAt(line, mapping.name),
      category: cellAt(line, mapping.category),
      barcode: cellAt(line, mapping.barcode),
      sellingPrice: cellAt(line, mapping.sellingPrice),
      costPrice: cellAt(line, mapping.costPrice),
      minStockAlert: cellAt(line, mapping.minStockAlert),
      currentStock: cellAt(line, mapping.currentStock),
      brand: cellAt(line, mapping.brand),
      description: cellAt(line, mapping.description),
      packagingValue: cellAt(line, mapping.packagingValue),
      packagingUnit: cellAt(line, mapping.packagingUnit),
    };

    const name = String(cells.name ?? '').trim();
    const importCategoryName = String(cells.category ?? '').trim();
    const isEmptyLine =
      !name &&
      !importCategoryName &&
      !String(cells.barcode ?? '').trim() &&
      cells.sellingPrice === undefined &&
      cells.costPrice === undefined &&
      cells.minStockAlert === undefined &&
      cells.currentStock === undefined;
    if (isEmptyLine) {
      rowIndex += 1;
      continue;
    }

    drafts.push({
      rowIndex,
      importCategoryName,
      name,
      barcode: String(cells.barcode ?? '').trim(),
      sellingPrice: parseOptionalNumber(cells.sellingPrice),
      costPrice: parseOptionalNumber(cells.costPrice),
      minStockAlert: parseRequiredInt(cells.minStockAlert, minStockFallback),
      currentStock: parseRequiredInt(cells.currentStock, 0),
      brand: String(cells.brand ?? '').trim(),
      description: String(cells.description ?? '').trim(),
      packagingValue: parseOptionalNumber(cells.packagingValue),
      packagingUnit: String(cells.packagingUnit ?? '').trim(),
    });
    rowIndex += 1;
  }

  return drafts;
}

/**
 * Ancien comportement : détection auto + parse (utilisé si pas d’étape manuelle).
 */
export function parseProductImportMatrix(
  rows: unknown[][],
  options?: ProductImportParseOptions
): ProductImportDraftRow[] {
  if (!rows.length) return [];

  let headerRowIndex = 0;
  const first = rows[0] || [];
  const fieldByCol: Record<number, ProductImportFieldKey> = {};
  for (let c = 0; c < first.length; c++) {
    const field = mapHeaderToField(first[c]);
    if (field) fieldByCol[c] = field;
  }
  if (Object.keys(fieldByCol).length < 2) {
    headerRowIndex = -1;
    for (let r = 0; r < Math.min(rows.length, 15); r++) {
      const trial: Record<number, ProductImportFieldKey> = {};
      const line = rows[r] || [];
      for (let c = 0; c < line.length; c++) {
        const field = mapHeaderToField(line[c]);
        if (field) trial[c] = field;
      }
      if (Object.keys(trial).length >= 2) {
        headerRowIndex = r;
        for (const k of Object.keys(fieldByCol)) {
          delete fieldByCol[Number(k)];
        }
        Object.assign(fieldByCol, trial);
        break;
      }
    }
  }

  if (headerRowIndex < 0) {
    throw new Error(
      'En-têtes non reconnus. Utilisez au minimum les colonnes « Nom » et « Catégorie » (voir export modèle).'
    );
  }

  const mapping: Partial<ProductImportColumnMapping> = {};
  for (const [colStr, field] of Object.entries(fieldByCol)) {
    const col = Number(colStr);
    (mapping as Record<ProductImportFieldKey, number>)[field] = col;
  }
  if (mapping.name === undefined || mapping.category === undefined) {
    throw new Error(
      'Colonnes « Nom » et « Catégorie » obligatoires. Utilisez l’étape de correspondance des colonnes.'
    );
  }

  return parseProductImportMatrixWithMapping(
    rows,
    headerRowIndex,
    mapping as ProductImportColumnMapping,
    options
  );
}

export type RowValidationIssue = { rowIndex: number; messages: string[] };

export function validateProductImportDrafts(
  drafts: ProductImportDraftRow[],
  categories: ProductCategory[],
  existingProductNamesLower: Set<string>,
  existingBarcodesLower: Set<string>,
  allowedPackagingUnits: string[] = []
): RowValidationIssue[] {
  const issues: RowValidationIssue[] = [];
  const nameCount = new Map<string, number>();
  const barcodeCount = new Map<string, number>();

  const categoryIds = new Set(categories.map((c) => c.id));

  for (const d of drafts) {
    const messages: string[] = [];
    if (!d.name.trim()) {
      messages.push('Le nom est obligatoire.');
    }
    if (!d.selectedCategoryId) {
      messages.push('Choisissez une catégorie.');
    } else if (!categoryIds.has(d.selectedCategoryId)) {
      messages.push('Catégorie invalide.');
    }
    if (d.minStockAlert < 0 || !Number.isInteger(d.minStockAlert)) {
      messages.push('Seuil alerte : entier ≥ 0.');
    }
    if (d.currentStock < 0 || !Number.isInteger(d.currentStock)) {
      messages.push('Stock : entier ≥ 0.');
    }
    if (d.sellingPrice !== undefined && (d.sellingPrice < 0 || !Number.isFinite(d.sellingPrice))) {
      messages.push('Prix de vente invalide.');
    }
    if (d.costPrice !== undefined && (d.costPrice < 0 || !Number.isFinite(d.costPrice))) {
      messages.push("Prix d'achat invalide.");
    }
    if (d.description.length > 500) {
      messages.push('Description trop longue (500 car. max).');
    }
    if (d.brand.length > 50) {
      messages.push('Marque trop longue (50 car. max).');
    }

    const packagingResult = resolveProductPackagingFields(
      {
        packagingValue: d.packagingValue,
        packagingUnit: d.packagingUnit || undefined,
      },
      allowedPackagingUnits
    );
    if (!packagingResult.valid) {
      messages.push(packagingResult.error);
    }

    const nl = d.name.trim().toLowerCase();
    if (nl) {
      nameCount.set(nl, (nameCount.get(nl) || 0) + 1);
    }
    const bc = d.barcode.trim().toLowerCase();
    if (bc) {
      barcodeCount.set(bc, (barcodeCount.get(bc) || 0) + 1);
    }

    if (messages.length) {
      issues.push({ rowIndex: d.rowIndex, messages });
    }
  }

  for (const d of drafts) {
    const nl = d.name.trim().toLowerCase();
    if (nl && nameCount.get(nl)! > 1) {
      const existing = issues.find((i) => i.rowIndex === d.rowIndex);
      const msg = 'Nom en double dans le fichier.';
      if (existing) {
        if (!existing.messages.includes(msg)) existing.messages.push(msg);
      } else {
        issues.push({ rowIndex: d.rowIndex, messages: [msg] });
      }
    }
    const bc = d.barcode.trim().toLowerCase();
    if (bc && barcodeCount.get(bc)! > 1) {
      const existing = issues.find((i) => i.rowIndex === d.rowIndex);
      const msg = 'Code-barres en double dans le fichier.';
      if (existing) {
        if (!existing.messages.includes(msg)) existing.messages.push(msg);
      } else {
        issues.push({ rowIndex: d.rowIndex, messages: [msg] });
      }
    }
    if (nl && existingProductNamesLower.has(nl)) {
      const existing = issues.find((i) => i.rowIndex === d.rowIndex);
      const msg = 'Un produit avec ce nom existe déjà dans le business.';
      if (existing) {
        if (!existing.messages.includes(msg)) existing.messages.push(msg);
      } else {
        issues.push({ rowIndex: d.rowIndex, messages: [msg] });
      }
    }
    if (bc && existingBarcodesLower.has(bc)) {
      const existing = issues.find((i) => i.rowIndex === d.rowIndex);
      const msg = 'Ce code-barres est déjà utilisé par un produit du business.';
      if (existing) {
        if (!existing.messages.includes(msg)) existing.messages.push(msg);
      } else {
        issues.push({ rowIndex: d.rowIndex, messages: [msg] });
      }
    }
  }

  return issues.sort((a, b) => a.rowIndex - b.rowIndex);
}

export function issuesByRowIndex(issues: RowValidationIssue[]): Map<number, string[]> {
  const m = new Map<number, string[]>();
  for (const i of issues) {
    m.set(i.rowIndex, i.messages);
  }
  return m;
}

export function hasBlockingIssues(issues: RowValidationIssue[]): boolean {
  return issues.length > 0;
}
